import type { BadgeColor } from '@vitesse/base-ui/components/templates';
import type { Integration, IntegrationForward, IntegrationForwardStatus } from '../../../core/domain/integration';

/*
 * WHERE THE DATA OF A MODEL GOES.
 *
 * A Forward is part of an integration. It writes each value that its selectors match to a table
 * in an outside system. The model page finds the Forwards that read the values of 1 model, so a
 * person can see that the data of the model leaves the gateway, where it goes and if it arrives.
 *
 * The page uses the integration roster that it already has. It needs no backend call.
 */

/** The selectors of the states and the status of 1 model. The runner publishes
 *  the state of the root machine, the state of each other machine and the
 *  status of the model as values (model-runner Subjects.ts). The 3 filters do
 *  not overlap, which the broker requires. */
export function modelStateSelectors(gatewayId: string, modelId: string): string[] {
  const base = `values.${gatewayId}.models.${modelId}`;
  return [`${base}.state`, `${base}.machines.*.state`, `${base}.status`];
}

/** True if 1 subject can match the 2 subject filters. `*` matches 1 token, and
 *  `>` matches 1 or more tokens at the end, as on the broker. */
export function filtersOverlap(a: string, b: string): boolean {
  const x = a.split('.');
  const y = b.split('.');
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    if (x[i] === '>') return y[i] !== undefined;
    if (y[i] === '>') return x[i] !== undefined;
    if (x[i] === undefined || y[i] === undefined) return false;
    if (x[i] !== '*' && y[i] !== '*' && x[i] !== y[i]) return false;
  }
  return true;
}

/** The state of 1 Forward: the state that the service reported, else why it
 *  has none. The service writes the statuses at most 1 time each 5 s, so a new
 *  Forward has no status for a short time. */
export function forwardState(i: Integration, st: IntegrationForwardStatus | undefined): string {
  if (st) return st.state;
  return i.status === 'connected' ? 'not reported yet' : 'stopped';
}

/** 1 Forward that reads the values of the model, with its integration. */
export interface ModelForward {
  integration: Integration;
  forward: IntegrationForward;
  /** Undefined until the service reports the Forward. */
  status?: IntegrationForwardStatus;
  /** From forwardState. */
  state: string;
}

/** The Forwards of the integrations that read the values of 1 model, sorted by
 *  the name of the integration and then by the name of the Forward. */
export function forwardsForModel(integrations: Integration[], gatewayId: string, modelId: string): ModelForward[] {
  if (!gatewayId || !modelId) return [];
  const mine = modelStateSelectors(gatewayId, modelId);
  const out: ModelForward[] = [];
  for (const i of integrations) {
    for (const f of i.forwards) {
      if (!f.selectors.some((s) => mine.some((m) => filtersOverlap(s, m)))) continue;
      const status = i.forwardStatus.find((s) => s.name === f.name);
      out.push({ integration: i, forward: f, status, state: forwardState(i, status) });
    }
  }
  const nameOf = (i: Integration) => i.displayName || i.id;
  return out.sort((a, b) => nameOf(a.integration).localeCompare(nameOf(b.integration)) || a.forward.name.localeCompare(b.forward.name));
}

/** The color of 1 Forward state: green when it writes, yellow when it starts or
 *  has no status yet, red when it has an error or does not run. */
export function forwardColor(state: string): BadgeColor {
  switch (state) {
    case 'running':
      return 'green';
    case 'starting':
    case 'not reported yet':
      return 'yellow';
    default:
      return 'red';
  }
}

/** The color of all the Forwards of a model: the worst color of 1 of them. */
export function forwardsColor(list: ModelForward[]): 'green' | 'yellow' | 'red' {
  const colors = list.map((f) => forwardColor(f.state));
  if (colors.includes('red')) return 'red';
  if (colors.includes('yellow')) return 'yellow';
  return 'green';
}
